import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
} from '@mui/material'
import { Cancel, SkipNext, Close, NotificationsActive, WarningAmber } from '@mui/icons-material'
import { SessionNodeDto, PendingInputDto } from '@api/types/session'
import { useSessionInput } from '../../../hooks/useSessionInput'
import { useModeratorStatus } from '../../../hooks/useSession'
import { useSessionStore } from '../../../stores/sessionStore'

interface ModeratorApprovalWaitProps {
  node: SessionNodeDto
  pendingInput: PendingInputDto
  onClose?: () => void
}

export default function ModeratorApprovalWait({
  node,
  pendingInput,
  onClose,
}: ModeratorApprovalWaitProps) {
  const { skipChallenge, cancelChallenge, isSubmitting } = useSessionInput(null)
  const adventureId = useSessionStore((s) => s.adventureId)
  const { data: moderatorStatus } = useModeratorStatus(adventureId)

  const handleSkip = () => {}
  const handleCancel = () => {}

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Main waiting content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 4,
          textAlign: 'center',
          position: 'relative',
        }}
      >
        {onClose && (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ position: 'absolute', top: 8, right: 8, color: 'text.disabled' }}
          >
            <Close fontSize="small" />
          </IconButton>
        )}

        {/* Animated icon ring */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            border: 3,
            borderColor: 'rgba(159,122,234,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2.5,
            animation: 'modPulse 3s ease-in-out infinite',
            '@keyframes modPulse': {
              '0%, 100%': {
                borderColor: 'rgba(159,122,234,0.3)',
                boxShadow: '0 0 0 0 rgba(159,122,234,0)',
              },
              '50%': {
                borderColor: 'rgba(159,122,234,0.6)',
                boxShadow: '0 0 20px 4px rgba(159,122,234,0.15)',
              },
            },
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #9f7aea, #7c3aed)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 26,
            }}
          >
            ⏲
          </Box>
        </Box>

        <Typography
          sx={{
            fontSize: '1.125rem',
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
          }}
        >
          {node.label || 'Waiting for Moderator'}
        </Typography>

        <Typography
          sx={{
            fontSize: '0.875rem',
            lineHeight: 1.6,
            color: 'text.secondary',
            mb: 2.5,
            maxWidth: 300,
          }}
        >
          {node.instructions || node.description || 'A moderator needs to verify your progress before you can continue to the next stage.'}
        </Typography>

        {/* Moderator offline warning */}
        {moderatorStatus && !moderatorStatus.hasOnlineModerator && (
          <Alert
            severity="warning"
            icon={<WarningAmber fontSize="small" />}
            sx={{
              mb: 2,
              maxWidth: 320,
              fontSize: '0.75rem',
              '& .MuiAlert-message': { fontSize: '0.75rem' },
            }}
          >
            No moderators are currently online. Approval may take longer than usual.
          </Alert>
        )}

        {/* Status badge */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'rgba(159,122,234,0.08)',
            border: 1,
            borderColor: 'rgba(159,122,234,0.2)',
            borderRadius: 6,
            px: 2.5,
            py: 1.25,
            mb: 2,
          }}
        >
          {/* Spinner */}
          <Box
            sx={{
              width: 14,
              height: 14,
              border: 2,
              borderColor: 'rgba(159,122,234,0.3)',
              borderTopColor: '#9f7aea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              '@keyframes spin': {
                to: { transform: 'rotate(360deg)' },
              },
            }}
          />
          <Typography
            sx={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: '#9f7aea',
            }}
          >
            Awaiting approval...
          </Typography>
        </Box>

        {/* Skip / Cancel */}
        {(pendingInput.allowSkip || pendingInput.allowCancel) && (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            {pendingInput.allowCancel && (
              <Button size="small" startIcon={<Cancel />} onClick={handleCancel} disabled={isSubmitting} sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                {pendingInput.cancelButtonText || 'Cancel'}
              </Button>
            )}
            {pendingInput.allowSkip && (
              <Button size="small" startIcon={<SkipNext />} onClick={handleSkip} disabled={isSubmitting} sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                {pendingInput.skipButtonText || 'Skip'}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Bottom bar */}
      <Box
        sx={{
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
          borderTop: 1,
          borderColor: 'divider',
          py: 1.75,
          px: 2,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', mb: 1 }}>
          Your moderator has been notified
        </Typography>
        <Button
          size="small"
          startIcon={<NotificationsActive sx={{ fontSize: 14 }} />}
          sx={{
            bgcolor: 'rgba(159,122,234,0.1)',
            border: 1,
            borderColor: 'rgba(159,122,234,0.25)',
            borderRadius: 5,
            color: '#9f7aea',
            fontSize: '0.75rem',
            fontWeight: 600,
            px: 2,
            py: 0.75,
            textTransform: 'none',
            '&:hover': { bgcolor: 'rgba(159,122,234,0.15)' },
          }}
        >
          Notify Again
        </Button>
      </Box>
    </Box>
  )
}
